import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Spacer from '@antimatterhq/ui/components/spacer';
import { AxiosError } from 'axios';
import { datadogLogs } from '@datadog/browser-logs';
import { DetectionRulesForm } from '../_partials/DetectionRulesForm';
import { AddDetectionRuleSchemaValues } from '@/zod/schemas/detectionRules/AddDetectionRule';
import {
  convertAddDetectionRuleValuesToRule,
  getAddDetectionRuleFormInitialValues,
  emptyRuleData,
} from '@/lib/detectionRules/detectionRulesHelper';
import { useCreateRule } from '@/hooks/core/useCreateRule';
import { BackLink } from '@/components/link/back/BackLink';
import { DATADOG_MESSAGE, getDatadogDetectionRule } from '@/utils/datadogUtils';
import { useGetRulesInfinite } from '@/hooks/core/useGetRulesInfinite';
import { AsyncValidationProvider } from '@/contexts/AsyncValidationContext';

export default function DetectionRulesAddPage() {
  const navigate = useNavigate();

  const { mutateAsync: createRuleAsync } = useCreateRule();
  const { optimisticUpdate } = useGetRulesInfinite();
  const initialValues = getAddDetectionRuleFormInitialValues(emptyRuleData);
  const [responseError, setResponseError] = useState<
    AxiosError<{ message: string }> | undefined
  >(undefined);

  const onSubmit = useCallback(
    async (values: AddDetectionRuleSchemaValues) => {
      setResponseError(undefined);
      const rule = convertAddDetectionRuleValuesToRule(values);
      await createRuleAsync(rule, {
        onSuccess: async () => {
          datadogLogs.logger.info(
            DATADOG_MESSAGE.DETECTION_RULE.CREATE.SUCCESS,
            getDatadogDetectionRule(rule),
          );
          optimisticUpdate(rule);
          navigate('/configure/detection-rules');
        },
        onError: async ({ error }) => {
          datadogLogs.logger.error(
            DATADOG_MESSAGE.DETECTION_RULE.CREATE.FAILURE,
            getDatadogDetectionRule(rule),
          );
          setResponseError(error as AxiosError<{ message: string }>);
        },
        toasts: {
          success: 'Rule created successfully',
          error: 'Failed to create rule',
        },
      });
    },
    [createRuleAsync, navigate, optimisticUpdate],
  );

  return (
    <AsyncValidationProvider>
      <main data-testid="detection-rules-add-page">
        <Spacer size="lg" />
        <div>
          <BackLink fallbackRoute="/configure/detection-rules" />
        </div>
        <Spacer size="base" />
        <DetectionRulesForm
          responseError={responseError}
          initialValues={initialValues}
          onSubmit={onSubmit}
        />
      </main>
    </AsyncValidationProvider>
  );
}
